import { useState } from 'react';
import { Check, ChevronDown, Copy, LogOut, User, Wallet } from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useIdentity, xUrl } from '@/api/identity';
import { LinkXDialog, useXLink } from '@/auth/xlink';
import { useChain } from '@/chain/ChainProvider';
import { shortAddr } from '@/lib/format';
import { WalletAvatar, XIcon } from '@/ui/bits';
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from '@/ui/primitives';
import { useActiveWallet } from './active';
import { devWalletAllowed, startDevWallet } from './devSession';

/** Header: "Log in" (Privy's modal), or the wallet's short address with its menu. */
export function WalletButton() {
  const w = useActiveWallet();
  const { config } = useChain();
  const navigate = useNavigate();
  const identity = useIdentity(w.kind === 'privy' ? w.address : null);
  const x = useXLink();
  const [linkOpen, setLinkOpen] = useState(false);

  if (!w.ready) {
    return (
      <button type="button" className="wallet-pill" disabled aria-busy="true">
        Log in
      </button>
    );
  }
  if (!w.address) {
    if (w.needsConnect) {
      return (
        <button type="button" className="wallet-pill is-login" onClick={w.connect}>
          <i /> Connect wallet
        </button>
      );
    }
    if (w.authenticated) {
      return (
        <button type="button" className="wallet-pill" onClick={() => void w.logout()} title="Log out">
          <i /> Setting up wallet…
        </button>
      );
    }
    return (
      <>
        {import.meta.env.DEV && devWalletAllowed(config.cluster) && (
          <button type="button" className="wallet-pill" onClick={() => void startDevWallet(config.cluster)} title="Local clone only: a burner key in this browser">
            Dev wallet
          </button>
        )}
        <button type="button" className="wallet-pill is-login" onClick={w.login}>
          <i /> Log in
        </button>
      </>
    );
  }

  const address = w.address;
  const handle = identity.data?.x.username;
  const copy = () =>
    void navigator.clipboard.writeText(address).then(
      () => toast.success('Address copied.'),
      () => toast.error('Copy failed: the browser blocked clipboard access.'),
    );
  return (
    <>
      <Menu>
        <MenuTrigger asChild>
          <button type="button" className="wallet-pill" aria-label={`Wallet ${address}`}>
            <WalletAvatar address={address} size={15} />
            <span className="num">{handle ? `@${handle}` : shortAddr(address, 4, 4)}</span>
            <ChevronDown size={12} />
          </button>
        </MenuTrigger>
        <MenuContent className="min-w-[230px]">
          <MenuLabel>{w.label}</MenuLabel>
          <MenuItem onSelect={copy}>
            <Copy className="size-4" /> <span className="num">{shortAddr(address, 6, 6)}</span>
          </MenuItem>
          <MenuItem onSelect={() => navigate(`/profile/${address}`)}>
            <User className="size-4" /> Profile
          </MenuItem>
          {w.kind === 'privy' && !x.off && (
            handle ? (
              <>
                <MenuItem asChild>
                  <a href={xUrl(handle)} target="_blank" rel="noreferrer noopener">
                    <XIcon className="size-4" /> @{handle}
                  </a>
                </MenuItem>
                <MenuItem disabled={x.busy} onSelect={() => void x.unlink()}>
                  <XIcon className="size-4" /> Unlink X
                </MenuItem>
              </>
            ) : (
              <MenuItem disabled={x.busy} onSelect={() => setLinkOpen(true)}>
                <XIcon className="size-4" /> Link X
              </MenuItem>
            )
          )}
          {w.options.length > 1 && (
            <>
              <MenuSeparator />
              <MenuLabel>Wallets</MenuLabel>
              {w.options.map((o) => (
                <MenuItem key={o.address} onSelect={() => w.select(o.address)}>
                  {o.address === address ? <Check className="size-4 text-accent" /> : <Wallet className="size-4" />}
                  <span className="flex-1">{o.label}</span>
                  <span className="num text-2xs text-fg-4">{shortAddr(o.address, 4, 4)}</span>
                </MenuItem>
              ))}
            </>
          )}
          <MenuSeparator />
          <MenuItem onSelect={() => void w.logout()}>
            <LogOut className="size-4" /> Log out
          </MenuItem>
        </MenuContent>
      </Menu>
      <LinkXDialog open={linkOpen} onOpenChange={setLinkOpen} wallets={w.options.map((o) => o.address)} />
    </>
  );
}
